import type { AssistantMessage, Draft } from '../reducers/assistant';
import type { PageState, Permission, ToolCall } from '../types';

/**
 * The store's vocabulary as one union.
 *
 * The action type *is* the string, rather than a constant pointing at it, so
 * that the reducer's `switch` is exhaustive to the compiler: a case that does
 * not exist is a type error, and a new action that no case handles is one too.
 */
export type AssistantAction =
  | { type: '@@INIT' }
  | { type: 'ASSISTANT_SET_FLOATING_OPEN'; open: boolean }
  | { type: 'ASSISTANT_SET_PERMISSION'; permission: Permission }
  | {
      type: 'ASSISTANT_RESET_CONVERSATION';
      conversationId: string | null;
      pathname: string | null;
    }
  | { type: 'ASSISTANT_ADD_MESSAGE'; message: AssistantMessage }
  | { type: 'ASSISTANT_APPEND_TOOL_CALL'; uid: string; toolCall: ToolCall }
  | {
      type: 'ASSISTANT_STREAM_MESSAGE';
      uid: string;
      content: string;
      segment: number;
    }
  | {
      type: 'ASSISTANT_COMPLETE_MESSAGE';
      uid: string;
      state: PageState | null;
      meta: Record<string, unknown> | null;
    }
  | { type: 'ASSISTANT_CANCEL_MESSAGE'; uid: string }
  | { type: 'ASSISTANT_SET_PENDING_FOCUS'; pendingFocus: boolean }
  | { type: 'ASSISTANT_SET_PENDING_RETRY'; pendingRetry: unknown }
  | { type: 'ASSISTANT_SET_DRAFT'; draft: Partial<Draft> }
  | { type: 'ASSISTANT_SET_TAB_INDEX'; tabIndex: number | null }
  | { type: 'ASSISTANT_SET_FOLLOW_CHANGES'; followChanges: boolean }
  | { type: 'ASSISTANT_SET_CONTEXT_BLOCK'; contextBlockId: string | null }
  | {
      type: 'ASSISTANT_LOAD_CONVERSATION';
      conversationId: string;
      pathname: string;
      messages: AssistantMessage[];
    };

export const setAssistantFloatingOpen = (open: boolean): AssistantAction => ({
  type: 'ASSISTANT_SET_FLOATING_OPEN',
  open,
});

export const setAssistantPermission = (
  permission: Permission,
): AssistantAction => ({ type: 'ASSISTANT_SET_PERMISSION', permission });

export const resetAssistantConversation = (
  conversationId: string | null,
  pathname: string | null,
): AssistantAction => ({
  type: 'ASSISTANT_RESET_CONVERSATION',
  conversationId,
  pathname,
});

export const addAssistantMessage = (
  message: AssistantMessage,
): AssistantAction => ({ type: 'ASSISTANT_ADD_MESSAGE', message });

export const appendAssistantToolCall = (
  uid: string,
  toolCall: ToolCall,
): AssistantAction => ({ type: 'ASSISTANT_APPEND_TOOL_CALL', uid, toolCall });

export const streamAssistantMessage = (
  uid: string,
  content: string,
  segment: number,
): AssistantAction => ({
  type: 'ASSISTANT_STREAM_MESSAGE',
  uid,
  content,
  segment,
});

export const completeAssistantMessage = (
  uid: string,
  state: PageState | null,
  meta: Record<string, unknown> | null,
): AssistantAction => ({
  type: 'ASSISTANT_COMPLETE_MESSAGE',
  uid,
  state,
  meta,
});

export const cancelAssistantMessage = (uid: string): AssistantAction => ({
  type: 'ASSISTANT_CANCEL_MESSAGE',
  uid,
});

export const setAssistantPendingFocus = (
  pendingFocus: boolean,
): AssistantAction => ({ type: 'ASSISTANT_SET_PENDING_FOCUS', pendingFocus });

/**
 * A denied turn re-sent with write from view mode first routes into edit
 * mode; the request parks here until the edit form is up.
 */
export const setAssistantPendingRetry = (
  pendingRetry: unknown,
): AssistantAction => ({ type: 'ASSISTANT_SET_PENDING_RETRY', pendingRetry });

/**
 * Unsent composer input (text, attachments, references). Lives in the store
 * so it survives the panel remounting — e.g. when the write toggle navigates
 * from view mode into edit mode.
 */
export const setAssistantDraft = (draft: Partial<Draft>): AssistantAction => ({
  type: 'ASSISTANT_SET_DRAFT',
  draft,
});

export const setAssistantTabIndex = (
  tabIndex: number | null,
): AssistantAction => ({ type: 'ASSISTANT_SET_TAB_INDEX', tabIndex });

export const setAssistantFollowChanges = (
  followChanges: boolean,
): AssistantAction => ({
  type: 'ASSISTANT_SET_FOLLOW_CHANGES',
  followChanges,
});

export const setAssistantContextBlock = (
  contextBlockId: string | null,
): AssistantAction => ({
  type: 'ASSISTANT_SET_CONTEXT_BLOCK',
  contextBlockId,
});

export const loadAssistantConversation = (
  conversationId: string,
  pathname: string,
  messages: AssistantMessage[],
): AssistantAction => ({
  type: 'ASSISTANT_LOAD_CONVERSATION',
  conversationId,
  pathname,
  messages,
});
